import React, { useMemo, useState, useCallback } from 'react'
import { makeStyles } from '@mui/styles'
import moment from 'moment'
import Markdown from 'react-markdown'
import { IconButton, Typography } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ThumbUp from '@mui/icons-material/ThumbUpOffAlt'
import ThumbUpFilled from '@mui/icons-material/ThumbUp'
import ThumbDown from '@mui/icons-material/ThumbDownOffAlt'
import ThumbDownFilled from '@mui/icons-material/ThumbDown'
import remarkGfm from 'remark-gfm'

import ThemeInterface from '@/theme-interface'
import { ChatMessage, FeedbackData, FeedbackRating } from '@/redux/ducks/ai/chat'
import config from '@/config'
import DotsLoader from '@/modules/base/components/loader/dots-loader'
import lang from '@/lang'
import AiChatAnswerLink from './ai-chat-answer-link'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import Direction from '@/enums/direction.enum'
import AiFeedbackTooltip from '@/modules/base/components/ai/chat/ai-feedback/ai-feedback-tooltip'
import AiFeedbackPanel from '@/modules/base/components/ai/chat/ai-feedback/ai-feedback-panel'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    alignSelf: 'flex-start',
    backgroundColor: theme.backgroundColorPrimaryLight,
    borderRadius: 30,
    borderBottomLeftRadius: 0,
    padding: '18px 20px',
    maxWidth: '80%',
    width: 'fit-content',
    wordBreak: 'break-word',
    flexDirection: 'column',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 5,
  },
  actions: {
    display: 'flex',
    gap: 10,
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  emptyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  emptyListItems: {
    margin: '0px 0px 0px 20px',
    listStyleType: 'inherit',
  },
  unorderedList: {
    listStyleType: 'disc',
    marginBlockStart: '1rem',
    marginBlockEnd: '1rem',
    paddingInlineStart: 40,
  },
}))

export default function AiChatAnswer(props: { message: ChatMessage }) {
  const { message } = props
  const classes = useStyles()

  const [feedbackExpanded, setFeedbackExpanded] = useState(false)
  const [pendingFeedbackType, setPendingFeedbackType] = useState<FeedbackRating>(null)
  const [submittedFeedback, setSubmittedFeedback] = useState<FeedbackData | null>(message?.feedback)

  const isLoading = useMemo(() => {
    return message.id === 'loading'
  }, [message.id])

  const handleCopyContent = () => {
    navigator.clipboard.writeText(message.content)
  }

  const handleThumbClick = useCallback((type: FeedbackRating) => {
    if (submittedFeedback) {
      return
    }

    setPendingFeedbackType(type)
    setFeedbackExpanded(true)
  }, [submittedFeedback])

  const renderFeedbackTooltipContent = useCallback(() => {
    if (!submittedFeedback) return null
    return <AiFeedbackTooltip submittedFeedback={submittedFeedback} />
  }, [submittedFeedback, classes])

  const handleFeedbackSubmit = useCallback((feedbackData: FeedbackData) => {
    setSubmittedFeedback(feedbackData)
    setFeedbackExpanded(false)
  }, [])

  const handleFeedbackCancel = useCallback(() => {
    setFeedbackExpanded(false)
    setPendingFeedbackType(null)
  }, [])

  const renderThumbButton = useCallback((type: FeedbackRating) => {
    const isUp = type === 'up'
    const isActive = submittedFeedback?.rating === type
    const isPending = pendingFeedbackType === type
    const isDisabled = submittedFeedback?.rating && !isActive
    const Icon = isUp
      ? (isActive || isPending ? ThumbUpFilled : ThumbUp)
      : (isActive || isPending ? ThumbDownFilled : ThumbDown)

    const button = (
      <IconButton
        size="small"
        onClick={() => handleThumbClick(type)}
        disabled={isDisabled}
        color={isActive || isPending ? 'primary' : undefined}
        disableRipple={Boolean(submittedFeedback)}
        style={{ cursor: submittedFeedback ? 'help' : 'pointer' }}
      >
        <Icon fontSize="small" />
      </IconButton>
    )

    if (isActive) {
      return (
        <RichTooltip
          content={renderFeedbackTooltipContent()}
          placement={Direction.TOP}
        >
          {button}
        </RichTooltip>
      )
    }

    return button
  }, [submittedFeedback, pendingFeedbackType, handleThumbClick, renderFeedbackTooltipContent, classes])

  const feedbackPanel = useMemo(() => {
    return (
      <AiFeedbackPanel
        messageId={message.id}
        feedbackExpanded={feedbackExpanded}
        pendingFeedbackType={pendingFeedbackType}
        onFeedbackSubmitted={handleFeedbackSubmit}
        onFeedbackCancelled={handleFeedbackCancel}
      />
    )
  }, [feedbackExpanded, pendingFeedbackType, handleFeedbackSubmit, handleFeedbackCancel])

  const footer = useMemo(() => {
    if (message.id === 'empty' || isLoading) {
      return null
    }

    return (
      <>
        <div className={classes.footer}>
          <Typography variant="caption">{moment(message.createdAt).format(config.datetimeFormat.dateTime24)}</Typography>
          <div className={classes.actions}>
            {renderThumbButton('up')}
            {renderThumbButton('down')}
            <IconButton size="small" onClick={handleCopyContent}><ContentCopyIcon fontSize="small" /></IconButton>
          </div>
        </div>
        {feedbackPanel}
      </>
    )
  }, [message.id, message.createdAt, message.content, renderThumbButton, feedbackPanel, classes])

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <div className={classes.loader}>
          <DotsLoader />
        </div>
      )
    }

    if (message.id === 'empty') {
      return (
        <div className={classes.empty}>
          <Typography variant="body1" dangerouslySetInnerHTML={{ __html: lang.ai.chat.empty.title }} />
          <Typography variant="body1" dangerouslySetInnerHTML={{ __html: lang.ai.chat.empty.description }} />
          <div className={classes.emptyList}>
            <ul className={classes.emptyListItems}>
              <li><b>{lang.ai.chat.empty.faq.title}</b> <a href={config.faqUrl} target="_blank" rel="noopener noreferrer">{lang.ai.chat.empty.faq.link}</a></li>
              <li><b>{lang.ai.chat.empty.supportTicket.title}</b> <a href={config.supportTicketUrl} target="_blank" rel="noopener noreferrer">{lang.ai.chat.empty.supportTicket.link}</a></li>
            </ul>
          </div>
          <Typography variant="body1" dangerouslySetInnerHTML={{ __html: lang.ai.chat.empty.hint }} />
        </div>
      )
    }

    return (
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: (props) => <AiChatAnswerLink {...props} />,
          ul: (props) => <ul className={classes.unorderedList} {...props} />,
        }}
      >
        {message.content}
      </Markdown>
    )
  }, [isLoading, message.content])

  return (
    <div className={classes.root}>
      {content}
      {footer}
    </div>
  )
}
